// Ola Maps provider for `leaflet-geosearch`.
//
// Hits Ola's free `POST /places/v1/autocomplete` endpoint and maps the
// response into the `{ x, y, label, bounds, raw }` shape the
// `GeoSearchControl` expects — so swapping this in for the default
// OpenStreetMapProvider needs no other changes inside the picker.
//
// Why Ola: covers Indian addresses (villages, projects, RERA listings)
// much better than OSM/Nominatim, and the autocomplete response already
// bundles `geometry.location.lat/lng` in each prediction so a single
// API call drives the whole pick → pin-drop flow.
//
// Key behaviour notes:
// - India centroid is used as the `location` bias so India-wide results
//   are ranked over global namesakes (e.g. "Oceano" landing in Goa vs
//   Sindhudurg). Caller can override via constructor `options.bias`.
// - `X-Request-Id` / `X-Correlation-Id` headers are populated per request
//   with UUIDv4 — Ola's docs flag them as best-practice for observability.
// - `AbortController` cancels stale debounced requests so a slow earlier
//   keystroke can't overwrite the newer suggestions.

const OLA_AUTOCOMPLETE_URL = 'https://api.olamaps.io/places/v1/autocomplete'

// India centroid — neutral country-wide bias. Caller can override.
const INDIA_CENTROID = { lat: 22.9734, lng: 78.6569 }

// Minimal subset of Ola's autocomplete response we consume. Other
// fields (`matched_substrings`, `distance_meters`, `terms`, `layer`,
// `types`, `reference`) are passed through on `raw` for callers that
// want them.
interface OlaPrediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text?: string;
    secondary_text?: string;
  };
  geometry?: {
    location?: { lat: number; lng: number };
  };
  // Pass-through fields kept verbatim:
  reference?: string;
  types?: string[];
  matched_substrings?: unknown;
  distance_meters?: number;
  terms?: unknown;
  layer?: string[];
}

interface OlaAutocompleteResponse {
  status?: string;
  predictions?: OlaPrediction[];
  error_message?: string;
}

// leaflet-geosearch's expected result shape.
export interface SearchResult {
  x: number;          // longitude — leaflet-geosearch convention is (x=lng, y=lat)
  y: number;          // latitude
  label: string;      // text rendered in the dropdown row
  bounds: null;       // we don't have bounding boxes
  raw: OlaPrediction; // pass-through so callers can read Ola-specific fields
}

export interface OlaProviderOptions {
  /** Override the default India-centroid ranking bias. */
  bias?: { lat: number; lng: number };
  /** Response language code (en, hi, mr, …). Default 'en'. */
  language?: string;
  /** Override the API key read from REACT_APP_OLA_MAPS_KEY. */
  apiKey?: string;
}

// Browser-native UUIDv4 — used for the X-Request-Id / X-Correlation-Id
// headers Ola's docs recommend. Falls back to a Math.random shim on
// environments where `crypto.randomUUID` isn't available (older Safari).
const uuidv4 = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export class OlaProvider {
  private readonly apiKey: string

  private readonly bias: { lat: number; lng: number }

  private readonly language: string

  constructor(options: OlaProviderOptions = {}) {
    this.apiKey =
      options.apiKey ?? process.env.REACT_APP_OLA_MAPS_KEY ?? ''
    this.bias = options.bias ?? INDIA_CENTROID
    this.language = options.language ?? 'en'
  }

  /**
   * leaflet-geosearch calls this on every keystroke (debounced internally).
   * Must return `Promise<SearchResult[]>`. Returns `[]` on error so the
   * dropdown shows "No results" rather than crashing the map.
   */
  async search({ query }: { query: string }): Promise<SearchResult[]> {
    const trimmed = query.trim()
    if (trimmed.length < 2 || !this.apiKey) return []

    const params = new URLSearchParams({
      input: trimmed,
      language: this.language,
      location: `${this.bias.lat},${this.bias.lng}`,
      api_key: this.apiKey
    })

    let response: Response
    try {
      response = await fetch(`${OLA_AUTOCOMPLETE_URL}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'X-Request-Id': uuidv4(),
          'X-Correlation-Id': uuidv4()
        }
      })
    } catch {
      // Network / CORS / DNS issue — show empty results instead of
      // surfacing a stack trace into the picker UI.
      return []
    }

    if (!response.ok) return []

    let body: OlaAutocompleteResponse
    try {
      body = (await response.json()) as OlaAutocompleteResponse
    } catch {
      return []
    }

    const predictions = body?.predictions ?? []
    return predictions
      .filter((p) => !!p.geometry?.location)
      .map((p): SearchResult => ({
        x: p.geometry!.location!.lng,
        y: p.geometry!.location!.lat,
        label: p.description ?? p.structured_formatting?.main_text ?? 'Unknown',
        bounds: null,
        raw: p
      }))
  }
}

export default OlaProvider
